import {
  LEFTOVER_KIND_KEYS,
  MISSING_RESULT_KEY,
  type MemberOutcome,
  blockedRowMessageKey,
  outcomeMessageKey,
  summarizeOutcomes,
} from '@/features/OrgMembers/lib/memberOutcomes'
import type { AddOrganizationMemberResult } from '@/generated/http-clients/v3ApiSchemas'
import { Box, List, Stack, Text } from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

const KEY_PREFIX = 'orgDetails.members.addMembers'

type AddMembersResultsProps = Readonly<{
  outcomes: MemberOutcome[]
}>

function sentColor(result: AddOrganizationMemberResult): string {
  if (result.outcome === 'CREATED' || result.outcome === 'ATTACHED_EXISTING') {
    return 'fg.success'
  }
  if (result.outcome === 'ALREADY_MEMBER') {
    return 'text.text-subtle'
  }
  return 'fg.error'
}

function notSentText(outcome: MemberOutcome, t: TFunction): string {
  const message = blockedRowMessageKey(outcome.row)
  const reason = message ? String(t(message.key, message.values ?? {})) : ''
  return String(t(`${KEY_PREFIX}.results.notSent`, { reason }))
}

function OutcomeLine({ outcome }: Readonly<{ outcome: MemberOutcome }>) {
  const { t } = useTranslation('features', { useSuspense: false })

  if (outcome.kind === 'notSent') {
    return (
      <Text fontSize="sm" color="fg.error">
        {notSentText(outcome, t)}
      </Text>
    )
  }

  if (outcome.kind === 'missing') {
    return (
      <Text fontSize="sm" color="fg.error">
        {t(MISSING_RESULT_KEY)}
      </Text>
    )
  }

  const { result } = outcome
  const leftovers = result.leftovers ?? []
  const showLeftovers =
    result.reason === 'CLEANUP_INCOMPLETE' && leftovers.length > 0

  return (
    <Stack gap={1}>
      <Text fontSize="sm" color={sentColor(result)}>
        {t(outcomeMessageKey(result))}
      </Text>
      {showLeftovers && (
        <Stack gap={1}>
          <Text fontSize="sm">{t(`${KEY_PREFIX}.results.leftovers`)}</Text>
          <List.Root fontSize="sm" ps={4}>
            {leftovers.map((leftover) => (
              <List.Item key={`${leftover.kind}-${leftover.id}`}>
                {`${t(LEFTOVER_KIND_KEYS[leftover.kind])}: ${leftover.id}`}
              </List.Item>
            ))}
          </List.Root>
        </Stack>
      )}
    </Stack>
  )
}

export function AddMembersResults({ outcomes }: AddMembersResultsProps) {
  const { t } = useTranslation('features', { useSuspense: false })
  const summary = summarizeOutcomes(outcomes)

  return (
    <Stack gap={4}>
      <Text fontWeight="medium">
        {t(`${KEY_PREFIX}.results.summary`, { ...summary })}
      </Text>
      <Stack as="ul" gap={2} listStyleType="none">
        {outcomes.map((outcome) => (
          <Box
            as="li"
            key={outcome.row.key}
            borderWidth="1px"
            borderRadius="md"
            p={3}
          >
            <Text
              fontWeight="medium"
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              {outcome.row.email}
            </Text>
            <OutcomeLine outcome={outcome} />
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}
